import { useSettings } from '../SettingsProvider';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useSaveSettings } from '@ais-ui/websockets';
import React, { useEffect, useState } from 'react';
import { useLocalization } from '@ais-ui/locale-management';
import {
  Button,
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  useToast,
} from '@ais-ui/ui-core';
import { cn } from '@ais-ui/utils';
import { ShapeSelect } from '../../Shapes/ShapeSelects/ShapeSelect';
import { useSettingsPage } from '../SettingsPageProvider';

const messageFormSchema = z.object({
  lookback_time_in_hours: z.number(),
  message_shape: z.string().optional(),
});

type MessageFormValues = z.infer<typeof messageFormSchema>;

export const MessageForm = () => {
  const { messages } = useLocalization();
  const { toast } = useToast();
  const { settings } = useSettings();

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const { mutate: mutateSettings } = useSaveSettings();

  const initialValues = {
    lookback_time_in_hours:
      settings['ribbonPlugin.settings'].msg_lookback_hours,
    message_shape: settings['ribbonPlugin.settings'].msg_area_of_interest,
  };

  const form = useForm<MessageFormValues>({
    resolver: zodResolver(messageFormSchema),
    defaultValues: initialValues,
  });

  const { setUnsavedSettings } = useSettingsPage();

  useEffect(() => {
    const subscription = form.watch((formValues) => {
      const hasChanges =
        JSON.stringify(formValues) !== JSON.stringify(initialValues);
      setUnsavedSettings(hasChanges);
    });

    return () => subscription.unsubscribe();
  }, [form, form.watch, initialValues, setUnsavedSettings]);

  function onSubmit(data: MessageFormValues) {
    setIsSubmitting(true);
    const settingsToSave = {
      ...settings,
      'ribbonPlugin.settings': {
        ...settings['ribbonPlugin.settings'],
        msg_lookback_hours: data.lookback_time_in_hours,
        msg_area_of_interest: data.message_shape,
      },
    };
    mutateSettings(settingsToSave, {
      onSuccess: () => {
        toast({
          className: 'update-messages-enable-success-toast',
          title: messages.genericToastTitleSuccess,
          description: messages.settingsMessageSuccessUpdateMessage,
        });
        setIsSubmitting(false);
        setUnsavedSettings(false);
      },
      onError: () => {
        toast({
          className: 'update-messages-settings-error-toast',
          title: messages.genericToastTitleError,
          description: messages.settingsMessageFailedUpdateMessage,
          variant: 'destructive',
        });
        setIsSubmitting(false);
      },
    });
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
        <div className="space-y-4">
          <FormField
            key="lookback_time_in_hours"
            control={form.control}
            name="lookback_time_in_hours"
            render={({ field }) => (
              <FormItem
                className={cn(
                  'flex flex-row items-center justify-between border rounded-lg p-4'
                )}
              >
                <div className="space-y-0.5">
                  <FormLabel className="text-base">
                    {messages.settingsMessageLookbackLabel}
                  </FormLabel>
                  <FormDescription>
                    {messages.settingsMessageLookbackDescription}{' '}
                  </FormDescription>
                </div>
                <FormControl
                  id="settings-message-lookback"
                  onChange={() => form.clearErrors()}
                >
                  <Input
                    className="w-20"
                    type="number"
                    {...field}
                    onChange={(e) => {
                      if (!e.target.value) {
                        field.onChange('');
                      } else {
                        // Convert the string value to a number
                        const numberValue = parseFloat(e.target.value);
                        field.onChange(numberValue);
                      }
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            key="message_shape"
            control={form.control}
            name="message_shape"
            render={({ field }) => (
              <FormItem
                className={cn(
                  'flex flex-row items-center justify-between border rounded-lg p-4'
                )}
              >
                <div className="space-y-0.5">
                  <FormLabel className="text-base">
                    {messages.settingsMessageShapeLabel}
                  </FormLabel>
                  <FormDescription>
                    {messages.settingsMessageShapeDescription}{' '}
                  </FormDescription>
                </div>
                <FormControl
                  id="settings-message-shape"
                  onChange={() => form.clearErrors()}
                >
                  <ShapeSelect
                    className="w-72"
                    id="message-shape-select"
                    value={field.value}
                    onChange={field.onChange}
                    allowedGeometryTypes={['Polygon']}
                  ></ShapeSelect>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <div className="flex items-center justify-end gap-2 pr-2">
          <Button
            id="settings-message-save-button"
            onClick={form.handleSubmit(onSubmit)}
            type="submit"
            disabled={isSubmitting}
          >
            {isSubmitting ? messages.genericSubmitting : messages.genericSubmit}
          </Button>
        </div>
      </form>
    </Form>
  );
};
